import { useMarkerVisibility } from 'hooks/map/useMarkerVisibility';
import { IPoint } from 'libs/utils/map/mapTypes';
import {
    getMarkerScale,
    isSinglePointMultiSelectable,
    SinglePointMultiSelectableMarker
} from 'libs/utils/world/worldUtils';
import { useContext, useEffect, useState } from 'react';
import { CtxEdit } from 'session/ctxEdit';

const doOverlap = (lt1: IPoint, rb1: IPoint, lt2: IPoint, rb2: IPoint) => {
    // if rectangle has area 0, no overlap
    return !(
        lt1.x + rb1.x - lt1.x < lt2.x ||
        lt1.y + rb1.y - lt1.y < lt2.y ||
        lt1.x > lt2.x + rb2.x - lt2.x ||
        lt1.y > lt2.y + rb2.y - lt2.y
    );
};

export const useWorldSelectionRectangle = (zoom: number) => {
    const ctxEdit = useContext(CtxEdit);
    const markerVisibility = useMarkerVisibility();

    const [selectRectangle, setSelectRectangle] = useState<{
        start: IPoint;
        current: IPoint;
    }>({ start: { x: 0, y: 0 }, current: { x: 0, y: 0 } });

    useEffect(() => {
        ctxEdit.setMouseDraggingSelection({
            left: Math.min(selectRectangle.start.x, selectRectangle.current.x),
            top: Math.min(selectRectangle.start.y, selectRectangle.current.y),
            height: Math.abs(selectRectangle.start.y - selectRectangle.current.y),
            width: Math.abs(selectRectangle.start.x - selectRectangle.current.x)
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectRectangle]);

    const isMarkerHit = (marker: SinglePointMultiSelectableMarker) => {
        const map = ctxEdit.mapRef?.current?.getMap();

        if (!map) return;

        const leftTop = {
            x: Math.min(selectRectangle.start.x, selectRectangle.current.x),
            y: Math.min(selectRectangle.start.y, selectRectangle.current.y)
        };
        const rightBottom = {
            x: Math.max(selectRectangle.start.x, selectRectangle.current.x),
            y: Math.max(selectRectangle.start.y, selectRectangle.current.y)
        };

        if (marker.elementType === 'image' && marker.elementData.renderAsLayer) {
            const layerData = marker.elementData.layerData;

            if (
                !layerData ||
                !layerData.bottomLeft ||
                !layerData.bottomRight ||
                !layerData.topLeft ||
                !layerData.topRight
            ) {
                return;
            }

            // This doesn't work well for rotated images, single selection is implemented better
            return doOverlap(
                map.project([layerData.topLeft.lng, layerData.topLeft.lat]),
                map.project([layerData.bottomRight.lng, layerData.bottomRight.lat]),
                leftTop,
                rightBottom
            );
        }

        const point = map.project([marker.lng, marker.lat]);

        const rectLeftTop = {
            x: point.x - (getMarkerScale(marker, zoom) * marker.ref?.current?.offsetWidth) / 2,
            y: point.y - (getMarkerScale(marker, zoom) * marker.ref?.current?.offsetHeight) / 2
        };
        const rectRightBottom = {
            x: point.x + (getMarkerScale(marker, zoom) * marker.ref?.current?.offsetWidth) / 2,
            y: point.y + (getMarkerScale(marker, zoom) * marker.ref?.current?.offsetHeight) / 2
        };

        const isValid = rectLeftTop.x && rectLeftTop.y && rectRightBottom.x && rectRightBottom.y;

        if (isValid && doOverlap(rectLeftTop, rectRightBottom, leftTop, rightBottom)) {
            return true;
        } else {
            return false;
        }
    };

    const getSelectedMarkers = () => {
        const singlePointSelectedMarkers = ctxEdit.worldMarkers
            .filter((e): e is SinglePointMultiSelectableMarker => isSinglePointMultiSelectable(e))
            .filter((e) => e.selectable && markerVisibility.isMarkerVisibleInEdit(e))
            .filter(isMarkerHit);

        return singlePointSelectedMarkers.map((e) => e.id);
    };

    return {
        startSelection: (x: number, y: number) => {
            setSelectRectangle({
                start: { x, y },
                current: { x, y }
            });
        },
        moveSelectionUpdate: (x: number, y: number) => {
            setSelectRectangle((value) => ({
                start: value.start,
                current: { x, y }
            }));
        },
        onMultiSelectionEnd: (handler: (selectedMarkerIds: string[]) => void) => {
            const markerIds = getSelectedMarkers();
            console.log('MarkerIds', markerIds);

            setSelectRectangle({
                start: { x: 0, y: 0 },
                current: { x: 0, y: 0 }
            });

            handler(markerIds);
        }
    };
};
